import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import {
  BottomSheetScrollView,
  BottomSheetTextInput,
} from '@gorhom/bottom-sheet';
import {
  Button,
  HelperText,
  TextInput as PaperTextInput,
} from 'react-native-paper';
import PhoneInput, {
  type ICountry,
  type IPhoneInputRef,
} from 'rn-international-phone-number';
import { GTFonts, useTheme } from '../../theme';
import { borderWidth, fs, s, v } from '../../utils/libs';
import { buildE164FromInput, PREFERRED_COUNTRIES } from '../../utils/phone';
import type { Customer } from '../../types/customers';
import InfoCallout from '../Reusables/InfoCallout';
import PersonAvatar from './PersonAvatar';
import PersonRow from './PersonRow';
import PickerPageHeader from './PickerPageHeader';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export interface NewCustomerFormData {
  name: string;
  /** E.164, or '' when left blank. */
  phone: string;
  email: string;
  /** Add as new: save even though customers with this name exist. */
  allowDuplicateName?: boolean;
}

interface NewCustomerPageProps {
  /** Name typed in the search, so it is not typed twice. */
  initialName?: string;
  subtitle?: string;
  /** Detected country for the phone field. */
  countryCode: string;
  country: ICountry | undefined;
  submitting: boolean;
  /**
   * Saves the form. Resolves with the customers who already have the name
   * when it went without a phone and was refused for it.
   */
  onSubmit: (data: NewCustomerFormData) => Promise<Customer[] | null> | void;
  /** One of those customers, picked instead of adding a new one. */
  onPickExisting: (customer: Customer) => void;
  onCancel: () => void;
  /** Inside another sheet's scroll view: no scroll view of its own. */
  embedded?: boolean;
}

// Paper's input drawn through the sheet's own input, so the bottom sheet
// moves with the keyboard (as Add Stock does).
const renderSheetInput = (props: any) => <BottomSheetTextInput {...props} />;

/**
 * The New customer form, built like Back Office › Add Customer: outlined
 * inputs, the same phone field, Cancel and a green save. Saves to the back
 * office and puts the customer on the order.
 */
const NewCustomerPage: React.FC<NewCustomerPageProps> = ({
  initialName = '',
  subtitle,
  countryCode,
  country,
  submitting,
  onSubmit,
  onPickExisting,
  onCancel,
  embedded = false,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [name, setName] = useState(initialName);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneE164, setPhoneE164] = useState('');
  const [phoneCountry, setPhoneCountry] = useState<ICountry | undefined>(
    country,
  );
  const [phoneValid, setPhoneValid] = useState(false);
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [email, setEmail] = useState('');
  // Customers who already have the name sent without a phone; a change to
  // the name or phone clears them.
  const [sameName, setSameName] = useState<Customer[] | null>(null);
  const phoneRef = useRef<IPhoneInputRef | null>(null);

  const inputOutlineProps = useMemo(
    () => ({
      outlineColor: theme.colors.borderGray,
      activeOutlineColor: theme.colors.primary,
    }),
    [theme],
  );

  const webPlaceholderTheme = useMemo(() => {
    if (Platform.OS !== 'web') return {};
    const hex = String(theme.colors.textSecondary || '');
    const placeholderColor = /^#[0-9a-f]{6}$/i.test(hex)
      ? `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}, 0.55)`
      : 'rgba(128, 128, 128, 0.55)';
    return {
      theme: {
        fonts: {
          regular: { fontFamily: GTFonts.regular, fontWeight: '400' as any },
        },
        colors: {
          placeholder: placeholderColor,
          onSurfaceVariant: placeholderColor,
        },
      },
    };
  }, [theme.colors.textSecondary]);

  useEffect(() => {
    if (!phoneCountry && country) setPhoneCountry(country);
  }, [country, phoneCountry]);

  useEffect(() => {
    setPhoneValid(!!phoneRef.current?.isValidPhoneNumber);
  }, [phoneInput, phoneE164]);

  const nameValid = name.trim().length >= 2;
  // Phone is optional: only a partly typed number blocks the save.
  const phoneOk = !phoneInput.trim() || phoneValid;
  const emailOk = !email.trim() || EMAIL_REGEX.test(email.trim());
  const canSubmit = nameValid && phoneOk && emailOk && !submitting;
  const showPhoneError = phoneTouched && phoneInput.length > 0 && !phoneValid;
  const showEmailError = email.trim().length > 0 && !emailOk;

  const submit = () => {
    if (!canSubmit) return;
    Promise.resolve(
      onSubmit({
        name: name.trim(),
        phone: phoneInput.trim() ? phoneE164 : '',
        email: email.trim(),
        ...(sameName ? { allowDuplicateName: true } : {}),
      }),
    ).then(matches => {
      if (matches?.length) setSameName(matches);
    });
  };

  const form = (
    <View style={[styles.body, embedded && styles.bodyEmbedded]}>
      <PaperTextInput
        mode="outlined"
        label="Customer name *"
        value={name}
        onChangeText={text => {
          setName(text);
          setSameName(null);
        }}
        placeholder="Enter customer name"
        placeholderTextColor={theme.colors.textSecondary}
        autoCapitalize="words"
        autoFocus={!initialName}
        returnKeyType="next"
        editable={!submitting}
        {...inputOutlineProps}
        {...webPlaceholderTheme}
        style={styles.input}
        outlineStyle={styles.inputOutline}
        render={renderSheetInput}
      />

      <View>
        <Text style={styles.fieldLabel}>Phone</Text>
        <View
          style={[
            styles.phoneInputOutline,
            phoneFocused && styles.phoneInputOutlineFocused,
            showPhoneError && styles.phoneInputOutlineError,
          ]}>
          <PhoneInput
            ref={phoneRef}
            defaultCountry={countryCode as any}
            value={phoneInput}
            modalType="popup"
            placeholder="720 222 222"
            popularCountries={PREFERRED_COUNTRIES}
            modalPopularCountriesTitle="Popular countries"
            modalAllCountriesTitle="All countries"
            disabled={submitting}
            onFocus={() => {
              setPhoneFocused(true);
              setPhoneTouched(true);
            }}
            onBlur={() => setPhoneFocused(false)}
            onChangePhoneNumber={value => {
              const next = value.startsWith('0') ? value.slice(1) : value;
              setPhoneInput(next);
              setSameName(null);
              setPhoneE164(
                buildE164FromInput(
                  next,
                  phoneCountry || phoneRef.current?.getCountry?.(),
                ),
              );
            }}
            country={phoneCountry}
            onChangeCountry={next => {
              setPhoneCountry(next);
              setPhoneE164(buildE164FromInput(phoneInput, next));
            }}
            phoneInputStyles={{
              container: {
                backgroundColor: 'transparent',
                height: '100%',
                alignItems: 'center',
              },
              flagContainer: {
                paddingHorizontal: s(8),
              },
              divider: {
                width: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
              },
              callingCode: {
                fontFamily: GTFonts.regular,
                fontSize: fs(16),
                lineHeight: fs(18),
                color: theme.colors.text,
              },
              input: {
                fontFamily: GTFonts.regular,
                fontSize: fs(16),
                lineHeight: fs(18),
                color: theme.colors.text,
                ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
              } as any,
            }}
          />
        </View>
        {showPhoneError ? (
          <HelperText type="error" visible style={styles.helper}>
            Invalid phone number
          </HelperText>
        ) : (
          <HelperText type="info" visible style={styles.helper}>
            Optional · helps you find them next time and avoids duplicates
          </HelperText>
        )}
      </View>

      <View>
        <PaperTextInput
          mode="outlined"
          label="Email address"
          value={email}
          onChangeText={text => setEmail(String(text || '').toLowerCase())}
          placeholder="Enter email address"
          placeholderTextColor={theme.colors.textSecondary}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          returnKeyType="done"
          onSubmitEditing={submit}
          editable={!submitting}
          error={showEmailError}
          {...inputOutlineProps}
          {...webPlaceholderTheme}
          style={styles.input}
          outlineStyle={styles.inputOutline}
          render={renderSheetInput}
        />
        {showEmailError ? (
          <HelperText type="error" visible style={styles.helper}>
            Enter a valid email
          </HelperText>
        ) : null}
      </View>

      {sameName ? (
        <View style={styles.sameName}>
          <Text style={styles.sameNameTitle}>
            A customer named {sameName[0].bp_customer_name} already exists
          </Text>
          <Text style={styles.sameNameNote}>
            Pick one for this order, or tap Add as new to save another customer
            with this name.
          </Text>
          <View style={styles.sameNameList}>
            {sameName.map(customer => (
              <PersonRow
                key={customer._id}
                leading={
                  <PersonAvatar name={customer.bp_customer_name} linked />
                }
                name={customer.bp_customer_name}
                meta={customer.bp_customer_phone_number || 'No phone'}
                onPress={() => onPickExisting(customer)}
                disabled={submitting}
              />
            ))}
          </View>
        </View>
      ) : null}

      <InfoCallout
        tone="info"
        message="Saved to Back Office › Customers and added to this order."
      />
    </View>
  );

  const footer = (
    <View style={[styles.footer, embedded && styles.footerEmbedded]}>
      <Button
        mode="outlined"
        onPress={onCancel}
        disabled={submitting}
        style={styles.actionButton}
        contentStyle={styles.actionButtonContent}
        labelStyle={styles.actionLabel}
        textColor={theme.colors.textSecondary}
        theme={{ colors: { outline: theme.colors.border } }}
        uppercase={false}>
        Cancel
      </Button>
      <Button
        mode="contained"
        onPress={submit}
        disabled={!canSubmit}
        loading={submitting}
        style={styles.actionButton}
        contentStyle={styles.actionButtonContent}
        labelStyle={styles.actionLabel}
        buttonColor={theme.colors.successDark}
        textColor={theme.colors.textInverse}
        uppercase={false}>
        {submitting
          ? 'Saving...'
          : sameName
            ? 'Add as new'
            : 'Save & add to order'}
      </Button>
    </View>
  );

  return (
    <View style={styles.page}>
      <PickerPageHeader
        title="New customer"
        subtitle={subtitle}
        onBack={submitting ? undefined : onCancel}
        compact={embedded}
      />
      {embedded ? (
        form
      ) : (
        <BottomSheetScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={styles.scroll}>
          {form}
        </BottomSheetScrollView>
      )}
      {footer}
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    page: {
      flex: 1,
    },
    scroll: {
      paddingBottom: v(16),
    },
    body: {
      paddingHorizontal: s(20),
      gap: v(14),
    },
    bodyEmbedded: {
      paddingHorizontal: s(4),
    },
    input: {
      backgroundColor: theme.colors.surface,
      height: v(50),
      fontSize: fs(16),
      ...(Platform.OS === 'web' && { height: v(56) }),
    },
    inputOutline: {
      borderRadius: theme.borderRadius.md,
    },
    fieldLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
      marginBottom: v(6),
    },
    phoneInputOutline: {
      backgroundColor: theme.colors.surface,
      height: v(50),
      borderRadius: theme.borderRadius.md,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: theme.colors.borderGray,
      flexDirection: 'row',
      alignItems: 'center',
      ...(Platform.OS === 'web' && { height: v(56) }),
    },
    // Same as paper's focused and error outlines on the inputs around it.
    phoneInputOutlineFocused: {
      borderWidth: 2,
      borderColor: theme.colors.primary,
    },
    phoneInputOutlineError: {
      borderWidth: 2,
      borderColor: theme.colors.error,
    },
    helper: {
      paddingHorizontal: s(4),
    },
    sameName: {
      gap: v(8),
    },
    sameNameTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    sameNameNote: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textGray600,
    },
    // Same card as the picker's locked customer.
    sameNameList: {
      borderRadius: s(16),
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderGray200,
      backgroundColor: theme.colors.surface,
      paddingHorizontal: s(2),
    },
    footer: {
      flexDirection: 'row',
      gap: s(8),
      paddingTop: v(12),
      paddingBottom: v(22),
      paddingHorizontal: s(20),
      borderTopWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      borderTopColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
    },
    footerEmbedded: {
      paddingHorizontal: s(4),
      paddingBottom: v(8),
      borderTopWidth: 0,
    },
    actionButton: {
      flex: 1,
      borderRadius: theme.borderRadius.md,
    },
    actionButtonContent: {
      height: v(50),
      justifyContent: 'center',
      alignItems: 'center',
    },
    actionLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      textAlign: 'center',
    },
  });

export default NewCustomerPage;
